import { Gender } from '../../../enum';
import { getFreeTrialAudio } from '../../../utils/free-trial/utils';

export type AuthState = 'checking' | 'guest' | 'student' | 'member';

export enum StatusEnum {
  NONE = 'NONE',
  /** 신청 완료 (체험 시작 전) */
  APPLIED = 'APPLIED',
  /** 체험 진행 중 (단계 진행 보고 수신) */
  IN_PROGRESS = 'IN_PROGRESS',
  /** 체험 완료 - AI 인증 합격 (암송 유형 판정) */
  COMPLETED = 'COMPLETED',
  /** 기간 만료 */
  EXPIRED = 'EXPIRED',
  /** 불합격 확정 - 응시 상한까지 모두 불합격 (분석 대기 회차가 있으면 확정하지 않음). DB enum 순서와 맞춰 맨 뒤 */
  FAILED = 'FAILED',
}

export enum RecitationType {
  /** 총점 95점 이상 */
  SAMUEL = 'SAMUEL',
  /** 정확도 90 이상 */
  SOLOMON = 'SOLOMON',
  /** 유창성(리듬) 90 이상 */
  DAVID = 'DAVID',
  /** 연습 5회 이상 후 합격 */
  JOSHUA = 'JOSHUA',
  /** 첫 응시 합격 */
  PETER = 'PETER',
  /** 불합격 후 재응시 합격 */
  MOSES = 'MOSES',
}

export enum StepEnum {
  LESSON = 'lesson',
  REVIEW = 'review',
  RECITATION = 'recitation',
  RESULT = 'result',
}

export const STEP_LABEL_MAP: Record<StepEnum, string> = {
  [StepEnum.LESSON]: '수업',
  [StepEnum.REVIEW]: '복습',
  [StepEnum.RECITATION]: '인증',
  [StepEnum.RESULT]: '결과',
};

export type FreeTrialStepProgress = Partial<Record<StepEnum, string>>;

export interface FreeTrialStatusItem {
  status: StatusEnum;
  linkToken: string;
  shareToken: string;
  shareAgreed: boolean;
  childName: string;
  expireAt: string;
  stepProgress: FreeTrialStepProgress | null;
  recitationType: RecitationType;
}

export const FREE_TRIAL_PROGRESS_STEPS = [
  {
    progressKey: StepEnum.LESSON,
    step: 1,
    label: '수업하기 중',
  },
  {
    progressKey: StepEnum.REVIEW,
    step: 2,
    label: '온라인 복습 중',
  },
  {
    progressKey: StepEnum.RECITATION,
    step: 3,
    label: 'AI 인증제 중',
  },
  {
    progressKey: StepEnum.RESULT,
    step: 4,
    label: '결과 리포트 확인',
  },
] as const;

export const TOTAL_FREE_TRIAL_STEP = FREE_TRIAL_PROGRESS_STEPS.length;

export interface FreeTrialQuotaItem {
  limit: number;
  used: number;
  remaining: number | null;
}

export enum FunnelTypeEnum {
  YOUTUBE = 'YOUTUBE',
  NAVER_SEARCH = 'NAVER_SEARCH',
  REFERRAL = 'REFERRAL',
  INSTAGRAM = 'INSTAGRAM',
  ETC = 'ETC',
}

export const FUNNEL_TYPE_LABEL_MAP: Record<FunnelTypeEnum, string> = {
  [FunnelTypeEnum.YOUTUBE]: '유튜브',
  [FunnelTypeEnum.NAVER_SEARCH]: '네이버 검색',
  [FunnelTypeEnum.REFERRAL]: '지인 추천',
  [FunnelTypeEnum.INSTAGRAM]: '인스타그램',
  [FunnelTypeEnum.ETC]: '기타',
};

export const FUNNEL_OPTIONS = Object.values(FunnelTypeEnum).map((value) => ({
  label: FUNNEL_TYPE_LABEL_MAP[value],
  value,
}));

export const BIRTH_YEAR_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const currentYear = new Date().getFullYear();
  const birthYear = currentYear - index - 5;
  const age = currentYear - birthYear + 1;

  return {
    label: `${birthYear}년생 (${age}세)`,
    value: birthYear,
  };
});

export interface FreeTrialApplyFormValues {
  childName: string;
  childGender: Gender | '';
  childBirthYear: string;
  funnel: FunnelTypeEnum | '';
  funnelDetail: string;
  marketingAgreed: boolean;
}

export interface ApplyFreeTrialRequest {
  childName: string;
  childGender: Gender;
  childBirthYear: number;
  funnel: FunnelTypeEnum;
  funnelDetail?: string;
  marketingAgreed: boolean;

  referrerShareToken?: string;
}

export interface ApplyFreeTrialResponse {
  applicationId: string;
  linkToken: string;
  reused: boolean;

  childName: string;
  subjectLabel: string;
  expireAt: string;
  remainingApplications: number;
}

export enum FreeTrialStep {
  LESSON = 1,
  REVIEW = 2,
  RECITATION = 3,
  RESULT = 4,
}

export const LESSON_STEPS = [
  '시작',
  '문장 1',
  '문장 2',
  '문장 3',
  'Watch & Listen',
  'Storytelling',
] as const;

export const LAST_LESSON_STEP = LESSON_STEPS.length - 1;

export interface SentenceSegment {
  text: string;
  highlight?: boolean;
}

export interface FingerPosition {
  className: string;
}

export interface StartScene {
  type: 'START';
  key: string;
  mode: 'READY' | 'BOOK_INTRO' | 'STORY_INTRO';
  audios?: string[];

  hint: string;
  guideTitle: string;
  guideMessage: string;
  guideTip: string;
}

export type SentenceActivity =
  | 'TALK'
  | 'LISTEN'
  | 'READ_REPEAT'
  | 'MEANING'
  | 'LISTEN_REPEAT'
  | 'POINT_REPEAT';

export interface SentenceMeaningItem {
  text: string;
  meaning: string;
  punctuation?: ',' | '.';
}

export interface FingerOrbitMotion {
  type: 'ORBIT';

  // 숫자 7의 중심
  centerX: number;
  centerY: number;

  // 원 크기: 컨테이너 높이 기준 %
  radius: number;

  // audio11 실제 길이에 맞춤
  durationSeconds: number;
}

export interface PointRepeatPoint {
  text: string;
  meaning: string;
  action: string;

  guideMessage: string; // 추가

  audio: string;

  finger: FingerPosition;

  fingerMotion?: FingerOrbitMotion;

  // part 전체 음원을 들을 때
  // 이 progress부터 이 손가락 위치를 사용
  cueProgress: number;
}

export interface PointRepeatPart {
  lineIndex: number;

  points: PointRepeatPoint[];

  // God finished his work, 같은 부분 전체 음원
  fullAudio: string;
}

export interface SentenceScene {
  type: 'SENTENCE';
  key: string;

  activity?: SentenceActivity;
  stage?: number;
  stageTitle?: string;

  audios?: string[];
  durationSeconds?: number;

  lines: SentenceSegment[][];
  dim: [number, number, number];
  textClassName: string;
  fingers: FingerPosition[];

  showSentence?: boolean;

  meaningItems?: SentenceMeaningItem[];
  meaningGroups?: SentenceMeaningItem[][];
  pointRepeatParts?: PointRepeatPart[];
  pointRepeatGuideClassName?: string;

  hint: string;
  guideTitle: string;
  guideMessage: string;
  guideTip: string;
}

export interface SectionIntroScene {
  type: 'SECTION_INTRO';
  key: string;
  title: string;
  subtitle: string;
  introText?: string;

  hint: string;
  guideTitle: string;
  guideMessage: string;
  guideTip: string;
}

export interface StoryReviewCue {
  cueProgress: number;
  dim: [number, number, number];
  hint: SentenceSegment[];
}

export interface StoryScene {
  type: 'STORY';
  key: string;
  audios?: string[];

  mode: 'WATCH' | 'STORYTELLING';
  faded: boolean;
  fingers?: FingerPosition[];

  durationSeconds?: number;

  startDelayMs?: number;
  reviewCues?: StoryReviewCue[];

  hint: string;
  guideTitle: string;
  guideMessage: string;
  guideTip: string;
}

export type LessonScene =
  | StartScene
  | SentenceScene
  | SectionIntroScene
  | StoryScene;

export const STORY_IMAGE =
  process.env.PUBLIC_URL + '/images/free-trial/lesson-2-story.jpeg';

export const FINGER_IMAGE =
  process.env.PUBLIC_URL + '/images/free-trial/finger.png';

export type ReadRepeatPhase = 'LISTEN' | 'REPEAT' | 'DONE';

export const LESSON_SCENE_MAP: Partial<Record<number, LessonScene[]>> = {
  0: [
    {
      type: 'START',
      key: 'ready',
      mode: 'READY',
      hint: '소리가 나오니 볼륨을 켜 주세요',
      guideTitle: '오늘의 수업을 시작해요',
      guideMessage: '“아이와 나란히 앉아 ‘수업 시작하기’를 눌러 주세요.”',
      guideTip:
        '수업은 엄마가 화면을 넘기며 진행해요. 각 단계에서 아이가 말할 시간을 충분히 주세요.',
    },
    {
      type: 'START',
      key: 'book-intro',
      mode: 'BOOK_INTRO',
      audios: [getFreeTrialAudio(1)],
      hint: '소리가 나오니 볼륨을 켜 주세요',
      guideTitle: '오늘의 수업을 시작해요',
      guideMessage: '“오늘 배울 책을 함께 들어 봐요.”',
      guideTip:
        '수업은 엄마가 화면을 넘기며 진행해요. 각 단계에서 아이가 말할 시간을 충분히 주세요.',
    },
    {
      type: 'START',
      key: 'story-intro',
      mode: 'STORY_INTRO',
      hint: '그림 세 칸을 하나씩 배워요',
      guideTitle: '오늘의 이야기',
      guideMessage:
        '“오늘은 하나님이 일곱째 날에 무엇을 하셨는지 배워 볼 거예요.”',
      guideTip: '‘다음’을 누르면 첫 번째 그림이 나와요.',
    },
  ],

  1: [
    {
      type: 'SENTENCE',
      key: 'sentence-1-talk',
      activity: 'TALK',
      stage: 1,
      stageTitle: '그림 보고 이야기하기',
      durationSeconds: 15,

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],
      dim: [0, 0.78, 0.78],
      textClassName:
        'absolute left-1/2 top-[18%] w-[92%] -translate-x-1/2 text-center',
      fingers: [],
      showSentence: false,

      hint: '문장 1 · 그림을 잘 살펴봐요',
      guideTitle: '그림 보고 이야기해요',
      guideMessage:
        '“그림에서 무엇이 보이는지, 어떤 내용일 것 같은지 이야기해 보세요.”',
      guideTip:
        '아이가 그림 속에서 본 것을 자유롭게 말하도록 기다려 주세요. 정답은 없어요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-1-listen',
      activity: 'LISTEN',
      stage: 2,
      stageTitle: '영어로 듣기',
      audios: [getFreeTrialAudio(2)],

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],
      dim: [0, 0.78, 0.78],
      textClassName:
        'absolute left-1/2 top-[18%] w-[92%] -translate-x-1/2 text-center',
      fingers: [],
      showSentence: false,

      hint: '문장 1 · 그림을 잘 살펴봐요',
      guideTitle: '영어로 들어 봐요',
      guideMessage: '“이 내용이 어떤 내용인지 영어로 들어 볼까요?”',
      guideTip: '잘 안 들렸다면 ‘다시’를 눌러 한 번 더 들어 보세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-1-read-repeat',
      activity: 'READ_REPEAT',
      stage: 3,
      stageTitle: '글자 보며 따라 하기',
      audios: [getFreeTrialAudio(2)],
      durationSeconds: 1.38,

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],
      dim: [0, 0.78, 0.78],
      textClassName:
        'absolute left-1/2 top-[22%] w-[92%] -translate-x-1/2 text-center',
      fingers: [],
      showSentence: true,

      hint: 'On the seventh day,',
      guideTitle: '글자 보며 따라 해요',
      guideMessage: '“자, 이제는 어떤 내용이었는지 글자를 보며 따라 할게요.”',
      guideTip:
        '빨간 글자는 강하고 길게 발음해 주세요. 영어는 리듬과 강세가 중요한 언어예요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-1-meaning',
      activity: 'MEANING',
      stage: 4,
      stageTitle: '뜻 알아보기',

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],
      dim: [0, 0.78, 0.78],
      textClassName:
        'absolute left-1/2 top-[22%] w-[92%] -translate-x-1/2 text-center',
      fingers: [],
      showSentence: true,

      meaningItems: [
        {
          text: 'On',
          meaning: '~에',
        },
        {
          text: 'the seventh day',
          meaning: '일곱째 날',
          punctuation: ',',
        },
      ],

      durationSeconds: 6,

      hint: 'On the seventh day,',
      guideTitle: '뜻을 알아봐요',
      guideMessage: '“On ~에, the seventh day 일곱째 날.',
      guideTip: '뜻 카드가 하나식 밝아지면 영어 → 우리말 순서로 읽어 주세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-1-listen-repeat',
      activity: 'LISTEN_REPEAT',
      stage: 5,
      stageTitle: '다시 듣고 따라 하기',
      audios: [getFreeTrialAudio(2)],
      durationSeconds: 1.38,

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],
      dim: [0, 0.78, 0.78],
      textClassName:
        'absolute left-1/2 top-[22%] w-[92%] -translate-x-1/2 text-center',
      fingers: [],
      showSentence: true,

      hint: 'On the seventh day,',
      guideTitle: '다시 듣고 따라 해요',
      guideMessage: '“다시 한번 듣고 따라 해 볼게요.”',
      guideTip:
        '‘따라 하세요!’가 나오면 아이가 소리 내어 말해요. 더 연습하고 싶으면 ‘다시’를 눌러 주세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-1-point-repeat',
      activity: 'POINT_REPEAT',
      stage: 6,
      stageTitle: '그림 짚으며 따라 하기',

      audios: [getFreeTrialAudio(2)],
      durationSeconds: 3,

      lines: [
        [
          { text: 'On the ' },
          { text: 'seventh day', highlight: true },
          { text: ',' },
        ],
      ],

      dim: [0, 0.78, 0.78],

      textClassName:
        'absolute left-1/2 top-[22%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      pointRepeatParts: [
        {
          lineIndex: 0,

          points: [
            {
              text: 'On the seventh day',
              meaning: '일곱째 날',
              action: '숫자 7을 짚어요',

              audio: getFreeTrialAudio(2),
              cueProgress: 0,

              finger: {
                className: 'left-[60%] top-[12%]',
              },

              guideMessage:
                '“On the seventh day는 일곱째 날이라는 뜻이에요. 그래서 숫자 7을 짚어요.”',
            },
          ],

          fullAudio: getFreeTrialAudio(2),
        },
      ],
      pointRepeatGuideClassName: 'bottom-[4%] left-1/2 -translate-x-1/2',

      hint: 'On the seventh day,',

      guideTitle: '왜 여기를 짚을까요?',
      guideMessage:
        '“On the seventh day는 일곱째 날이라는 뜻이에요. 그래서 숫자 7을 짚어요.”',

      guideTip:
        '그림 위 말풍선을 보며 왜 그곳을 짚는지 아이에게 설명해 주세요.',
    },
  ],

  2: [
    {
      type: 'SENTENCE',
      key: 'sentence-2-talk',
      activity: 'TALK',
      stage: 1,
      stageTitle: '그림 보고 이야기하기',
      durationSeconds: 15,

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: false,

      hint: '문장 2 · 그림을 잘 살펴봐요',

      guideTitle: '그림 보고 이야기해요',

      guideMessage:
        '“그림에서 무엇이 보이는지, 어떤 일이 일어나고 있는지 이야기해 보세요.”',

      guideTip:
        '아이가 그림 속 행동을 자유롭게 이야기하도록 기다려 주세요. 정답은 없어요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-2-listen',
      activity: 'LISTEN',
      stage: 2,
      stageTitle: '영어로 듣기',

      audios: [getFreeTrialAudio(3)],

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: false,

      hint: '문장 2 · 그림을 잘 살펴봐요',

      guideTitle: '영어로 들어 봐요',

      guideMessage: '“이 내용이 어떤 내용인지 영어로 들어 볼까요?”',

      guideTip: '잘 안 들렸다면 ‘다시’를 눌러 한 번 더 들어 보세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-2-read-repeat',
      activity: 'READ_REPEAT',
      stage: 3,
      stageTitle: '글자 보며 따라 하기',

      audios: [getFreeTrialAudio(4), getFreeTrialAudio(5)],

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      hint: 'God finished his work, and rested.',

      guideTitle: '글자 보며 따라 해요',

      guideMessage: '“자, 이제는 어떤 내용이었는지 글자를 보며 따라 할게요.”',

      guideTip:
        '빨간 글자는 강하고 길게 발음해 주세요. 영어는 리듬과 강세가 중요한 언어예요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-2-meaning',
      activity: 'MEANING',
      stage: 4,
      stageTitle: '뜻 알아보기',

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      meaningGroups: [
        [
          {
            text: 'God',
            meaning: '하나님은',
          },
          {
            text: 'finished',
            meaning: '끝마치셨어요',
          },
          {
            text: 'his work',
            meaning: '그의 일을',
            punctuation: ',',
          },
        ],
        [
          {
            text: 'and',
            meaning: '그리고',
          },
          {
            text: 'rested',
            meaning: '쉬셨어요',
            punctuation: '.',
          },
        ],
      ],

      hint: 'God finished his work, and rested.',

      guideTitle: '뜻을 알아봐요',

      guideMessage:
        '“God finished his work 하나님이 하시던 일을 마치셨어요, and rested 그리고 쉬셨어요.”',

      guideTip: '뜻 카드가 하나씩 밝아지면 영어 → 우리말 순서로 읽어 주세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-2-listen-repeat',
      activity: 'LISTEN_REPEAT',
      stage: 5,
      stageTitle: '다시 듣고 따라 하기',

      audios: [getFreeTrialAudio(4), getFreeTrialAudio(5)],

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      hint: 'God finished his work, and rested.',

      guideTitle: '다시 듣고 따라 해요',

      guideMessage: '“다시 한번 듣고 따라 해 볼게요.”',

      guideTip:
        '‘따라 하세요!’가 나오면 아이가 소리 내어 말해요. 더 연습하고 싶으면 ‘다시’를 눌러 주세요.',
    },

    {
      type: 'SENTENCE',
      key: 'sentence-2-point-repeat',
      activity: 'POINT_REPEAT',
      stage: 6,
      stageTitle: '그림 짚으며 따라 하기',

      // 6-2에서도 사용할 예정이라 일단 유지
      audios: [getFreeTrialAudio(4), getFreeTrialAudio(5)],

      durationSeconds: 3,

      lines: [
        [
          { text: 'God finished ', highlight: true },
          { text: 'his ' },
          { text: 'work', highlight: true },
          { text: ',' },
        ],
        [{ text: 'and ' }, { text: 'rested', highlight: true }, { text: '.' }],
      ],

      dim: [0.78, 0, 0.78],

      textClassName:
        'absolute bottom-[2%] left-1/2 w-[94%] -translate-x-1/2 text-center',

      fingers: [],

      showSentence: true,

      pointRepeatParts: [
        {
          lineIndex: 0,

          points: [
            {
              text: 'God',
              meaning: '하나님',
              action: '하나님을 짚어요',
              guideMessage: '“God, 하나님이에요. 하나님을 짚어 볼까요?”',
              audio: getFreeTrialAudio(6),
              cueProgress: 0,
              finger: {
                className: 'left-[70%] top-[32%]',
              },
            },
            {
              text: 'finished',
              meaning: '끝내셨어요',
              action: '일을 끝내고 탁탁 터는 손',
              guideMessage:
                '“finished는 끝냈다는 뜻이에요. 일을 끝내고 손을 탁탁 터는 것 같은 하나님의 손을 짚어요.”',
              audio: getFreeTrialAudio(7),
              cueProgress: 24,
              finger: {
                className: 'left-[45%] top-[45%]',
              },
            },
            {
              text: 'his work',
              meaning: '하나님이 만드신 것',
              action: '동물과 자연',
              guideMessage:
                '“his work는 하나님이 만드신 창조물이에요. 여기 있는 동물과 자연을 짚어요.”',
              audio: getFreeTrialAudio(8),
              cueProgress: 53,
              finger: {
                className: 'left-[60%] top-[70%]',
              },
            },
          ],

          fullAudio: getFreeTrialAudio(4),
        },

        {
          lineIndex: 1,

          points: [
            {
              text: 'and rested',
              meaning: '그리고 쉬셨어요',
              action: '편안히 감은 눈',
              guideMessage:
                '“and rested는 쉬셨다는 뜻이에요. 편안히 쉬시는 것 같은 하나님의 감은 눈을 짚어요.”',
              audio: getFreeTrialAudio(5),
              cueProgress: 0,
              finger: {
                className: 'left-[73%] top-[28%]',
              },
            },
          ],

          fullAudio: getFreeTrialAudio(5),
        },
      ],
      pointRepeatGuideClassName: 'top-[4%] left-1/2 -translate-x-1/2',

      hint: 'God finished his work, and rested.',

      guideTitle: '왜 여기를 짚을까요?',
      guideMessage: '',

      guideTip:
        '그림 위 말풍선을 보며 왜 그곳을 짚는지 아이에게 설명해 주세요.',
    },
  ],

  3: [
    // 1단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-talk',
      activity: 'TALK',
      stage: 1,
      stageTitle: '그림 보고 이야기하기',

      durationSeconds: 15,

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[55%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: false,

      hint: '문장 3 · 그림을 잘 살펴봐요',

      guideTitle: '그림 보고 이야기해요',
      guideMessage:
        '“그림에서 무엇이 보이는지, 어떤 내용일 것 같은지 이야기해 보세요.”',
      guideTip:
        '아이가 그림 속에서 본 것을 자유롭게 이야기하도록 기다려 주세요. 정답은 없어요.',
    },

    // 2단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-listen',
      activity: 'LISTEN',
      stage: 2,
      stageTitle: '영어로 듣기',

      audios: [getFreeTrialAudio(9)],

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[55%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: false,

      hint: '문장 3 · 그림을 잘 살펴봐요',

      guideTitle: '영어로 들어 봐요',
      guideMessage: '“이 내용이 어떤 내용인지 영어로 들어 볼까요?”',
      guideTip: '잘 안 들렸다면 ‘다시’를 눌러 한 번 더 들어 보세요.',
    },

    // 3단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-read-repeat',
      activity: 'READ_REPEAT',
      stage: 3,
      stageTitle: '글자 보며 따라 하기',

      audios: [getFreeTrialAudio(10), getFreeTrialAudio(11)],

      durationSeconds: 3,

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[65%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      hint: 'God blessed the day and made it holy.',

      guideTitle: '글자 보며 따라 해요',
      guideMessage: '“자, 이제는 어떤 내용이었는지 글자를 보며 따라 할게요.”',
      guideTip:
        '빨간 글자는 강하고 길게 발음해 주세요. 영어는 리듬과 강세가 중요한 언어예요.',
    },

    // 4단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-meaning',
      activity: 'MEANING',
      stage: 4,
      stageTitle: '뜻 알아보기',

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[65%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      meaningGroups: [
        [
          {
            text: 'God',
            meaning: '하나님은',
          },
          {
            text: 'blessed',
            meaning: '축복하셨어요',
          },
          {
            text: 'the day',
            meaning: '그 날을',
          },
        ],
        [
          {
            text: 'and',
            meaning: '그리고',
          },
          {
            text: 'made',
            meaning: '만드셨어요',
          },
          {
            text: 'it',
            meaning: '그것을',
          },
          {
            text: 'holy',
            meaning: '거룩하게',
            punctuation: '.',
          },
        ],
      ],

      hint: 'God blessed the day and made it holy.',

      guideTitle: '뜻을 알아봐요',
      guideMessage:
        '“God 하나님은, blessed 축복하셨어요, the day 그 날을, and 그리고, made 만드셨어요, it 그것을, holy 거룩하게.”',
      guideTip: '뜻 카드가 하나씩 밝아지면 영어 → 우리말 순서로 읽어 주세요.',
    },

    // 5단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-listen-repeat',
      activity: 'LISTEN_REPEAT',
      stage: 5,
      stageTitle: '다시 듣고 따라 하기',

      audios: [getFreeTrialAudio(10), getFreeTrialAudio(11)],

      durationSeconds: 3,

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[65%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      hint: 'God blessed the day and made it holy.',

      guideTitle: '다시 듣고 따라 해요',
      guideMessage: '“다시 한번 듣고 따라 해 볼게요.”',
      guideTip:
        '‘따라 하세요!’가 나오면 아이가 소리 내어 말해요. 더 연습하고 싶으면 ‘다시’를 눌러 주세요.',
    },

    // 6단계
    {
      type: 'SENTENCE',
      key: 'sentence-3-point-repeat',
      activity: 'POINT_REPEAT',
      stage: 6,
      stageTitle: '그림 짚으며 따라 하기',

      audios: [getFreeTrialAudio(10), getFreeTrialAudio(11)],

      durationSeconds: 3,

      lines: [
        [
          { text: 'God blessed ', highlight: true },
          { text: 'the ' },
          { text: 'day', highlight: true },
        ],
        [
          { text: 'and ' },
          { text: 'made', highlight: true },
          { text: ' it ' },
          { text: 'holy', highlight: true },
          { text: '.' },
        ],
      ],

      dim: [0.78, 0.78, 0],

      textClassName:
        'absolute left-1/2 top-[65%] w-[92%] -translate-x-1/2 text-center',

      fingers: [],
      showSentence: true,

      pointRepeatParts: [
        {
          lineIndex: 0,

          points: [
            {
              text: 'God',
              meaning: '하나님',
              action: '하나님이 계신 빛',

              guideMessage:
                '“God, 하나님은 빛 가운데 계실 것 같지요? 빛을 짚어요.”',

              audio: getFreeTrialAudio(12),

              cueProgress: 0,

              finger: {
                className: 'left-[81%] top-[87%]',
              },
            },

            {
              text: 'blessed the day',
              meaning: '축복하신 날',
              action: '바로 일곱째 날, 7!',

              guideMessage:
                '“blessed the day, 빛이 축복하고 있는 날은 어디일까요? 맞아요, 일곱째 날! 7을 다시 짚어요.”',

              audio: getFreeTrialAudio(13),

              // audio10에서 손가락이 이 시점부터 이동
              cueProgress: 35,

              finger: {
                className: 'left-[48%] top-[91%]',
              },
            },
          ],

          fullAudio: getFreeTrialAudio(10),
        },

        {
          lineIndex: 1,

          points: [
            {
              text: 'and made it holy',
              meaning: '그 날을 거룩하게 하셨어요',
              action: '7에 동그라미!',

              guideMessage:
                '“and made it holy, 그날을 거룩하게 하셨대요. 7에 동그라미를 쳐 봐요!',

              audio: getFreeTrialAudio(11),

              cueProgress: 0,

              finger: {
                className: 'left-[51%] top-[93%]',
              },

              fingerMotion: {
                type: 'ORBIT',

                centerX: 52,
                centerY: 97,

                radius: 6,

                // audio11 길이에 맞춰 조정
                durationSeconds: 1.8,
              },
            },
          ],

          fullAudio: getFreeTrialAudio(11),
        },
      ],

      pointRepeatGuideClassName: 'top-[4%] left-1/2 -translate-x-1/2',

      hint: 'God blessed the day and made it holy.',

      guideTitle: '왜 여기를 짚을까요?',
      guideMessage: '',

      guideTip:
        '그림 위 말풍선을 보며 왜 그곳을 짚는지 아이에게 설명해 주세요.',
    },
  ],

  4: [
    {
      type: 'SECTION_INTRO',
      key: 'watch-intro',

      title: "Today's Story",
      subtitle: 'Watch & Listen',
      introText: '처음부터 쭉 들어요',

      hint: '오늘 배운 이야기를 한 번에 들어요',

      guideTitle: '처음부터 쭉 들어요',
      guideMessage:
        '“이제 오늘 배운 내용을 처음부터 쭉 들어 볼게요. 들으면서 동시에 따라 해 보세요.”',
      guideTip: '‘다음’을 누르면 바로 시작해요.',
    },

    {
      type: 'STORY',
      key: 'watch-story',

      audios: [getFreeTrialAudio(14)],

      mode: 'WATCH',

      // 처음에는 전체 흐림
      faded: true,

      startDelayMs: 1000,

      reviewCues: [
        {
          cueProgress: 0,

          // 문장 1 → 위만 선명
          dim: [0, 0.78, 0.78],

          hint: [
            { text: 'On the ' },
            { text: 'seventh day', highlight: true },
            { text: ',' },
          ],
        },

        {
          // audio14에서 문장 2가 시작되는 실제 %
          cueProgress: 20,

          // 위 + 가운데 선명
          dim: [0, 0, 0.78],

          hint: [
            { text: 'God finished ', highlight: true },
            { text: 'his ' },
            { text: 'work', highlight: true },
            { text: ', ' },
            { text: 'and ' },
            { text: 'rested', highlight: true },
            { text: '.' },
          ],
        },

        {
          // audio14에서 문장 3이 시작되는 실제 %
          cueProgress: 57,

          // 전체 선명
          dim: [0, 0, 0],

          hint: [
            { text: 'God blessed ', highlight: true },
            { text: 'the ' },
            { text: 'day', highlight: true },
            { text: ' and ' },
            { text: 'made', highlight: true },
            { text: ' it ' },
            { text: 'holy', highlight: true },
            { text: '.' },
          ],
        },
      ],

      hint: '오늘 배운 이야기를 처음부터 들어요',

      guideTitle: '들으면서 따라 해요',

      guideMessage: '“들으면서 동시에 따라 해 보세요.”',

      guideTip: '그림 칸이 열리는 순서대로 이야기가 흘러가요.',
    },
  ],

  5: [
    {
      type: 'SECTION_INTRO',
      key: 'storytelling-intro',

      title: "Today's Story",
      subtitle: 'Storytelling',
      introText: '스스로 말해요',

      hint: '이번에는 음원 없이 말해요',

      guideTitle: '스스로 말해요',

      guideMessage: '“이제 음원 없이 스스로 그림을 짚어 가며 말해 보세요.”',

      guideTip: '‘다음’을 누르면 그림이 나와요.',
    },
    {
      type: 'STORY',
      key: 'storytelling-story',

      mode: 'STORYTELLING',

      faded: false,

      durationSeconds: 20,

      fingers: [],

      hint: '그림을 처음부터 끝까지 짚으며 스스로 말해요',

      guideTitle: '그림 짚으며 말해요',

      guideMessage: '“이제 음원 없이 스스로 그림을 짚어 가며 말해 보세요.”',

      guideTip: '막히면 ‘힌트 보기’를 눌러 짚을 곳을 손가락으로 보여 주세요.',
    },
  ],
};

export const STORYTELLING_HINT_POINTS = [1, 2, 3].flatMap((step) =>
  (LESSON_SCENE_MAP[step] ?? [])
    .filter(
      (scene): scene is SentenceScene =>
        scene.type === 'SENTENCE' && scene.activity === 'POINT_REPEAT',
    )
    .flatMap((scene) =>
      (scene.pointRepeatParts ?? []).flatMap((part) => part.points),
    ),
);

export const TRIAL_STEPS = [
  {
    step: '1',
    title: '수업하기',
    description: '시작 전 안내',
    isActive: true,
  },
  {
    step: '2',
    title: '온라인 복습',
    description: '잠겨 있어요',
    isActive: false,
  },
  {
    step: '3',
    title: 'AI 인증제',
    description: '잠겨 있어요',
    isActive: false,
  },
  {
    step: '★',
    title: '결과 리포트',
    description: '잠겨 있어요',
    isActive: false,
  },
];

export interface CurriculumBook {
  book: string;
  title: string;
  description?: string;
  tags?: string[];
}

export interface CurriculumLevel {
  level: string;
  books: CurriculumBook[];
}

export interface CurriculumGroup {
  id: string;
  title: string;
  range: string;
  variant: string; // 'PURPLE' | 'YELLOW';
  levels: CurriculumLevel[];
}

export const COURSES = [
  {
    title: '잉스바',
    description: '바이블 스토리를 그림으로 암송하며 말씀을 마음 판에 새겨요.',
    badge: '오늘 체험 중',
    curriculumDescription:
      '잉스바(English Story Bible)는 정철 선생님의 발명특허 삽화기억술로 그림과 짝지어진 청크의 소리와 뜻을 기억하며 바이블 스토리를 암송하는 과목이에요. 구약 24권, 신약 24권을 따라가며 Book 1의 쉬운 문장에서 시작해 점점 길고 다양한 구조의 문장을 암송하게 돼요.',
    isActive: true,
    curriculums: [
      {
        id: 'old-testament',
        title: '구약',
        range: 'Book 1 ~ 24',
        variant: 'PURPLE',
        levels: [
          {
            level: 'Level 1 · Starting',
            books: [
              {
                book: 'Book 1',
                title: 'In the Beginning',
                description: '천지창조 · 아담과 이브 · 가인과 아벨',
              },
              {
                book: 'Book 2',
                title: 'Heaven or Hell?',
                description: '지옥과 천국',
              },
              {
                book: 'Book 3',
                title: "Noah's Ark",
                description: '노아의 방주',
              },
              {
                book: 'Book 4',
                title: 'Language',
                description: '바벨탑 · 언어 분리 · 성령의 불과 방언',
              },
            ],
          },
          {
            level: 'Level 2 · Standing',
            books: [
              {
                book: 'Book 5',
                title: 'Abraham and Isaac',
                description: '아브라함과 이삭',
              },
              {
                book: 'Book 6',
                title: 'Joseph Sold as a Slave',
                description: '야곱과 열두 아들 · 요셉의 꿈 · 노예로 팔림',
              },
              {
                book: 'Book 7',
                title: 'Brothers Bowed Down to Joseph',
                description:
                  '요셉의 이집트 노예 생활 · 꿈 해석 · 형들과의 재회',
              },
              {
                book: 'Book 8',
                title: 'Moses in the Desert',
                description:
                  '요셉 가족의 이집트 이주 · 모세의 광야 생활 40년 · 가나안으로 이동',
              },
            ],
          },
          {
            level: 'Level 3 · Walking',
            books: [
              {
                book: 'Book 9',
                title: 'Ten Plagues and the Passover',
                description: '이집트의 열 가지 재앙 · 유월절',
              },
              {
                book: 'Book 10',
                title: 'The Red Sea',
                description: '불기둥과 물기둥 · 홍해의 기적',
              },
              {
                book: 'Book 11',
                title: 'Food from Heaven',
                description: '가나안으로의 40년 여정 · 메추리와 만나',
              },
              {
                book: 'Book 12',
                title: 'Ten Commandments',
                description: '십계명',
              },
            ],
          },
          {
            level: 'Level 4 · Running',
            books: [
              {
                book: 'Book 13',
                title: 'Twelve Spies',
                description: '열두 정탐꾼',
              },
              {
                book: 'Book 14',
                title: 'The Jordan and Jericho',
                description:
                  '모세의 죽음과 여호수아의 등장 · 요단강 기적 · 여리고성 함락',
              },
              {
                book: 'Book 15',
                title: 'Stolen Treasure',
                description: '하나님의 것을 훔친 아간 · 아이성 함락',
              },
              {
                book: 'Book 16',
                title: 'Judges',
                description: '이스라엘의 사사들 (옷니엘, 에훗, 드보라)',
              },
            ],
          },
          {
            level: 'Level 5 · Jumping',
            books: [
              {
                book: 'Book 17',
                title: 'Gideon and Samson',
                description: '기드온과 삼손',
              },
              {
                book: 'Book 18',
                title: 'Saul and David',
                description: '사무엘이 사울과 다윗에게 기름 부음',
              },
              {
                book: 'Book 19',
                title: 'David and Goliath',
                description: '다윗과 골리앗',
              },
              {
                book: 'Book 20',
                title: 'King David and His Songs',
                description:
                  '사울의 질투와 죽음 · 왕이 된 다윗의 찬양 (시편 23편)',
              },
            ],
          },
          {
            level: 'Level 6 · Flying',
            books: [
              {
                book: 'Book 21',
                title: "Solomon's Wisdom",
                description: '솔로몬의 판결',
              },
              {
                book: 'Book 22',
                title: 'Fire from Heaven',
                description: '엘리야의 응답',
              },
              {
                book: 'Book 23',
                title: 'Jonah in the Big Fish',
                description: '요나',
              },
              {
                book: 'Book 24',
                title: 'Daniel and the Hungry Lions',
                description: '다니엘과 사자',
              },
            ],
          },
        ],
      },
      {
        id: 'new-testament',
        title: '신약',
        range: 'Book 25 ~ 48',
        variant: 'YELLOW',
        levels: [
          {
            level: 'Level 7',
            books: [
              {
                book: 'Book 25',
                title: 'An Angel Visits Mary',
                description: '천사가 마리아를 찾아옴 · 세례 요한',
              },
              {
                book: 'Book 26',
                title: 'Jesus Is Born',
                description: '예수님의 탄생',
              },
              {
                book: 'Book 27',
                title: 'Jesus and John',
                description:
                  '세례 요한이 예수님께 세례를 줌 · 40일 광야와 마귀의 시험 (1)',
              },
              {
                book: 'Book 28',
                title: 'Jesus, Devil, and Twelve Apostles',
                description: '마귀의 시험 (2) · 열두 제자',
              },
            ],
          },
          {
            level: 'Level 8',
            books: [
              {
                book: 'Book 29',
                title: 'Jesus Teaches and Heals',
                description:
                  '가버나움에서 가르치심 · 귀신을 내쫓고 병을 고치심',
              },
              {
                book: 'Book 30',
                title: 'Water Turns Into Wine',
                description: '물을 포도주로 바꾸신 기적',
              },
              {
                book: 'Book 31',
                title: "The Lord's Prayer",
                description: '주기도문',
              },
              {
                book: 'Book 32',
                title: 'Take Your Mat and Walk! / Go Wash Your Eyes!',
                description: '중풍병자를 고치심 · 실로암에서 눈먼 자를 고치심',
              },
            ],
          },
          {
            level: 'Level 9',
            books: [
              {
                book: 'Book 33',
                title: 'Teaching on a Mountainside (1)',
                description:
                  '산상수훈 (소금 비유, 원수 사랑, 은밀한 구제, 하늘에 쌓은 보물)',
              },
              {
                book: 'Book 34',
                title: 'Teaching on a Mountainside (2)',
                description:
                  '산상수훈 (구하라 찾으라 두드리라, 좁은 문, 반석 위에 지은 집)',
              },
              {
                book: 'Book 35',
                title: 'Jesus Heals the Sick and Calms the Storm',
                description: '병자를 고치심 · 파도를 잠잠케 하심',
              },
              {
                book: 'Book 36',
                title: 'Jesus Feeds 5,000',
                description: '오병이어',
              },
            ],
          },
          {
            level: 'Level 10',
            books: [
              {
                book: 'Book 37',
                title: 'The Good Samaritan',
                description: '선한 사마리아인',
              },
              {
                book: 'Book 38',
                title: "Jesus's Appearance Is Changed",
                description:
                  '베드로의 고백 · 예수님께서 영광스러운 모습으로 변하심',
              },
              {
                book: 'Book 39',
                title: 'The Story of the Lost Son',
                description: '잃어버린 아들(탕자) 비유',
              },
              {
                book: 'Book 40',
                title: 'Jesus Raises Lazarus',
                description: '나사로 이야기',
              },
            ],
          },
          {
            level: 'Level 11',
            books: [
              {
                book: 'Book 41',
                title: 'Jesus Enters Jerusalem',
                description: '예수님의 예루살렘 입성',
              },
              {
                book: 'Book 42',
                title:
                  'Jesus Cleans the Temple / Mary Pours Perfume / Judas Betrays Jesus',
                description: '성전 청소 · 향유 옥합 · 유다의 배반',
              },
              {
                book: 'Book 43',
                title: 'The Last Supper 1',
                description: '최후의 만찬 · 제자들의 발을 씻기심',
              },
              {
                book: 'Book 44',
                title: 'The Last Supper 2',
                description: '최후의 만찬 · 포도나무 비유 · 성령의 일',
              },
            ],
          },
          {
            level: 'Level 12',
            books: [
              {
                book: 'Book 45',
                title: 'Jesus Is Arrested',
                description: '겟세마네 기도 · 예수님의 체포',
              },
              {
                book: 'Book 46',
                title: 'Jesus Dies',
                description: '십자가에서 돌아가심',
              },
              {
                book: 'Book 47',
                title: 'Jesus Rises Again',
                description: '예수님의 부활',
              },
              {
                book: 'Book 48',
                title: 'Jesus Goes Up to Heaven',
                description: '예수님의 승천',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: '잉크 · 잉코',
    description:
      '문장 구조는 스피킹 드릴로, 디테일은 소문법으로, 중등 입시 영문법까지 완성해요.',
    badge: '문법',
    curriculumDescription:
      '잉크(English Creative)에서는 영어 문장 구조를 스피킹 드릴로 익히는 대문법 수업을 하고, 잉코(English Correct)에서는 디테일한 문법을 잡는 소문법 수업을 해요. 두 과목을 함께 하며 중등 입시에 필요한 영문법을 완성합니다.',
    isActive: false,
    curriculums: [
      {
        id: 'english-creative',
        title: '잉크(English Creative)',
        range: 'Book 1 ~ 24 · 대문법 24권',
        variant: 'PURPLE',
        levels: [
          {
            level: 'Level 1 · Starting',
            books: [
              {
                book: 'Book 1',
                title: '주어 + 동사',
              },
              {
                book: 'Book 2',
                title: '3인칭 주어 + 동사 · 주어 + 동사 + α',
              },
              {
                book: 'Book 3',
                title: '주어 + 동사 + α · 동사 과거형 · 목적격 대명사',
              },
              {
                book: 'Book 4',
                title: '지각동사 · be동사',
              },
            ],
          },
          {
            level: 'Level 2 · Standing',
            books: [
              {
                book: 'Book 5',
                title:
                  'a / an / 복수의 s · 소유격 대명사 · 정관사 · 전명구 추가',
              },
              {
                book: 'Book 6',
                title: '현재진행 (분사구) · be동사 과거',
              },
              {
                book: 'Book 7',
                title: 'be동사 부정 · 일반동사 부정',
              },
              {
                book: 'Book 8',
                title: '부정문 종합 비교 · be동사 의문문 · 일반동사 의문문',
              },
            ],
          },
          {
            level: 'Level 3 · Walking',
            books: [
              {
                book: 'Book 9',
                title:
                  '일반동사 과거 · 과거 의문문 · there is / are · a / some / a lot of · there 의문문 · these / those',
              },
              {
                book: 'Book 10',
                title: '4형식 · 5형식 · 조동사 (can · will)',
              },
              {
                book: 'Book 11',
                title: '조동사 (be going to) · 명령 · 제안 / 부탁',
              },
              {
                book: 'Book 12',
                title:
                  '조동사 (should · must · had better · have to · may · used to)',
              },
            ],
          },
          {
            level: 'Level 4 · Running',
            books: [
              {
                book: 'Book 13',
                title: '수동태 · 비교급 / 최상급',
              },
              {
                book: 'Book 14',
                title: '비교급 / 최상급 · 분사',
              },
              {
                book: 'Book 15',
                title: '부사 · 대명사 · to부정사 (형용사 / 부사)',
              },
              {
                book: 'Book 16',
                title: 'to부정사 (형용사 / 부사)',
              },
            ],
          },
          {
            level: 'Level 5 · Jumping',
            books: [
              {
                book: 'Book 17',
                title: 'to부정사 (-하는 것) · 동명사',
              },
              {
                book: 'Book 18',
                title: '동명사 (과거지향) · 의문사',
              },
              {
                book: 'Book 19',
                title: '관계대명사 · 부가의문문',
              },
              {
                book: 'Book 20',
                title:
                  '간접의문문 · 관계대명사 what · 관계부사 · 등위접속사 · 종속접속사 부사절',
              },
            ],
          },
          {
            level: 'Level 6 · Flying',
            books: [
              {
                book: 'Book 21',
                title: '종속접속사 부사절',
              },
              {
                book: 'Book 22',
                title: '종속접속사 부사절 + 가정 · 종속접속사 that',
              },
              {
                book: 'Book 23',
                title: '종속접속사 that · if · whether · 현재완료',
              },
              {
                book: 'Book 24',
                title: '현재완료 · 종합 독해',
              },
            ],
          },
        ],
      },

      {
        id: 'english-correct',
        title: '잉코(English Correct)',
        range: 'Book 25 ~ 48 · 소문법 24권',
        variant: 'YELLOW',
        levels: [
          {
            level: 'Level 7',
            books: [
              {
                book: 'Book 25',
                title: '명사',
                tags: [
                  '명사의 개념',
                  '가산명사 · 불가산명사',
                  '관사 a',
                  '관사 an',
                  '단수명사 · 복수명사',
                  '복수명사 만드는 법',
                ],
              },
              {
                book: 'Book 26',
                title: '명사',
                tags: [
                  '불규칙 복수명사',
                  '단위 + 물질명사',
                  '고유명사',
                  '명사 소유격 만들기',
                  '명사의 역할: 주어',
                  '명사의 역할: 목적어',
                ],
              },
              {
                book: 'Book 27',
                title: '동사 · 현재시제',
                tags: [
                  '동사',
                  '동사원형과 명령문',
                  '인칭대명사 주어 정리',
                  '일반동사 3인칭 단수',
                  '일반동사 부정문 · 의문문',
                  'Be동사',
                  '일반동사와 Be동사 비교',
                ],
              },
              {
                book: 'Book 28',
                title: '동사 · 과거시제',
                tags: [
                  'Be동사 과거',
                  'Be동사 과거 부정문',
                  'Be동사 과거 의문문',
                  '일반동사 과거',
                  '일반동사 과거 부정문',
                  '일반동사 과거 의문문',
                ],
              },
            ],
          },

          {
            level: 'Level 8',
            books: [
              {
                book: 'Book 29',
                title: '대명사',
                tags: [
                  '인칭대명사의 격',
                  '소유격 of',
                  '이중소유격',
                  '재귀대명사 1 · 2',
                  '지시대명사 1 · 2',
                  '비인칭주어 It',
                ],
              },
              {
                book: 'Book 30',
                title: '숫자',
                tags: [
                  '숫자',
                  '기수',
                  '배수사',
                  'Telling Time',
                  'Telling Date and Year',
                  '정관사 (1) · (2)',
                  '무관사',
                ],
              },
              {
                book: 'Book 31',
                title: '대명사와 형용사',
                tags: [
                  'One and Ones',
                  'Other (1) · (2) · (3)',
                  'Some and Any',
                  'No and Not Any',
                  'Many vs Much',
                  'Few / A Few vs Little / A Little',
                ],
              },
              {
                book: 'Book 32',
                title: '형용사',
                tags: [
                  'All and Every',
                  'Pronoun + of',
                  '명사 앞에 쓰는 형용사',
                  '보어로서의 형용사',
                  '명사 + (l)y = 형용사',
                  '형용사 후치 수식',
                  'the + 형용사',
                  '부사의 개념',
                ],
              },
            ],
          },

          {
            level: 'Level 9',
            books: [
              {
                book: 'Book 33',
                title: '부사',
                tags: [
                  '부사의 형태',
                  '형용사 = 부사 (1) · (2)',
                  '빈도부사',
                  '전치사 at',
                  '전치사 on',
                  '전치사 in',
                  '전치사 by',
                ],
              },
              {
                book: 'Book 34',
                title: '전치사',
                tags: [
                  '전치사 시간',
                  '전치사 위치',
                  '전치사 움직임',
                  '전치사 이유',
                  '전명구',
                  '이어동사 (1) · (2) · (3)',
                ],
              },
              {
                book: 'Book 35',
                title: '준동사 to',
                tags: [
                  'to부정사 개념',
                  'to부정사 형용사 용법',
                  'to부정사 부사 용법 (1)~(4)',
                  'to부정사 명사 용법 (1) · (2)',
                ],
              },
              {
                book: 'Book 36',
                title: '준동사 to / ing',
                tags: [
                  '의문사 + to부정사',
                  'to부정사의 부정',
                  'to부정사의 의미상 주어 (1) · (2)',
                  'ing (1) · (2) · (3)',
                  '동명사',
                ],
              },
            ],
          },

          {
            level: 'Level 10',
            books: [
              {
                book: 'Book 37',
                title: '준동사 pp',
                tags: [
                  '명사 + 명사',
                  'to V와 ing 비교 (1) · (2)',
                  '수동태에 쓰이는 과거분사',
                  '능동과 수동',
                  '수동태의 관용적 표현',
                  '현재완료에 쓰이는 과거분사',
                  '과거분사 총정리',
                ],
              },
              {
                book: 'Book 38',
                title: '문장의 형식',
                tags: [
                  '1형식',
                  '2형식 (1) · (2)',
                  '3형식',
                  '4형식',
                  '5형식 (1) · (2) · (3)',
                ],
              },
              {
                book: 'Book 39',
                title: '시제',
                tags: [
                  '기본형',
                  '진행형',
                  '완료형',
                  '완료진행형',
                  '진행 수동태',
                  '완료 수동태',
                  '조동사 (1) · (2)',
                ],
              },
              {
                book: 'Book 40',
                title: '조동사',
                tags: [
                  '조동사 (3)~(6)',
                  'used to와 would',
                  'do',
                  '유사조동사',
                  '조동사 수동태',
                ],
              },
            ],
          },

          {
            level: 'Level 11',
            books: [
              {
                book: 'Book 41',
                title: '비교급',
                tags: [
                  'as ~ as',
                  'as ~ as 표현',
                  '비교급 (1) · (2)',
                  '이중 비교급',
                  '비교급 강조',
                  '배수사 비교표현',
                  '최상급 (1)',
                ],
              },
              {
                book: 'Book 42',
                title: '관계사',
                tags: [
                  '최상급 (2)',
                  '주격 관계대명사 (1) · (2)',
                  '목적격 관계대명사',
                  '전치사 + 관계사 (1) · (2)',
                  '관계사 whose',
                  '관계사와 쉼표',
                ],
              },
              {
                book: 'Book 43',
                title: '접속사',
                tags: [
                  '절',
                  '관계부사',
                  '의문사 + ever',
                  'No matter + 의문사',
                  '분리구조 의문문',
                  'That, Whether 그리고 If',
                  'It ~ that 강조구문',
                  '접속사',
                ],
              },
              {
                book: 'Book 44',
                title: '접속사',
                tags: [
                  '명령문 + and / or',
                  '상관접속사',
                  '접속 부사 (1) · (2)',
                  '시간 접속사 (1) · (2)',
                  '이유 접속사',
                  '양보 접속사',
                ],
              },
            ],
          },

          {
            level: 'Level 12',
            books: [
              {
                book: 'Book 45',
                title: '접속사',
                tags: [
                  '조건 접속사',
                  '결과를 나타내는 so ~ that',
                  '목적을 나타내는 so that',
                  'that 총정리',
                  '접속사 + 분사구문',
                  'as (1) · (2)',
                  'as if',
                ],
              },
              {
                book: 'Book 46',
                title: '가정법',
                tags: [
                  '가정법 (1)~(4)',
                  'I Wish',
                  'I recommend that',
                  '감탄문',
                  '부가의문문',
                ],
              },
              {
                book: 'Book 47',
                title: '기타 구문',
                tags: [
                  '동격',
                  '일치 (1) · (2)',
                  '화법',
                  '도치 (1) · (2)',
                  '생략',
                  '가주어 · 가목적어 it',
                ],
              },
              {
                book: 'Book 48',
                title: '문법 독해 (총정리)',
                tags: [
                  '나아만 이야기',
                  '엘리사와 아람인들',
                  '하나님의 왕국',
                  '코스 수료',
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: '잉포',
    description: '초등 교과 회화부터 현지에서 바로 쓰는 확장 회화까지 배워요.',
    badge: '회화',
    curriculumDescription:
      '잉포 198(English Formula 198)에서는 초등 영어 교과에서 다루는 포뮬러 회화(상황별로 바로 꺼내 쓰는 정형 표현)를 모두 익혀요. 이어서 잉포 Plus(English Formula Plus)에서는 현지 생활에서 더 폭넓게 활용할 수 있는 확장된 포뮬러 회화를 배웁니다.',
    isActive: false,
    curriculums: [
      {
        id: 'english-formula-198',
        title: '잉포 198(English Formula 198)',
        range: 'Book 1 ~ 24',
        variant: 'PURPLE',
        levels: [
          {
            level: 'Level 1',
            books: [
              { book: 'Book 1', title: 'Greetings and Meeting' },
              { book: 'Book 2', title: 'Going to a Birthday Party' },
              { book: 'Book 3', title: 'Scheduling an Appointment' },
              { book: 'Book 4', title: 'Seeing Animals at the Zoo' },
            ],
          },
          {
            level: 'Level 2',
            books: [
              { book: 'Book 5', title: 'Going to an Ice Cream Shop' },
              { book: 'Book 6', title: 'Going for a Bike Ride' },
              { book: 'Book 7', title: 'Shopping at the Mall' },
              { book: 'Book 8', title: 'Preparing Dinner at Home' },
            ],
          },
          {
            level: 'Level 3',
            books: [
              { book: 'Book 9', title: 'Making New Friends' },
              { book: 'Book 10', title: 'Watching a Movie at Home' },
              { book: 'Book 11', title: 'Going to the Hospital' },
              { book: 'Book 12', title: 'Talking About Teeth' },
            ],
          },
          {
            level: 'Level 4',
            books: [
              { book: 'Book 13', title: 'Talking About Family' },
              { book: 'Book 14', title: 'Going to a Restaurant' },
              { book: 'Book 15', title: 'Playing Baseball' },
              { book: 'Book 16', title: 'Going to an Art Museum' },
            ],
          },
          {
            level: 'Level 5',
            books: [
              { book: 'Book 17', title: 'Vegetable Gardening' },
              { book: 'Book 18', title: 'Going Downtown' },
              { book: 'Book 19', title: 'Going to an Amusement Park' },
              { book: 'Book 20', title: 'Going to an Aquarium' },
            ],
          },
          {
            level: 'Level 6',
            books: [
              { book: 'Book 21', title: 'Making up with a Friend' },
              { book: 'Book 22', title: 'Doing Household Chores' },
              { book: 'Book 23', title: 'Talking About Eyesight' },
              { book: 'Book 24', title: 'Planning a Family Trip Abroad' },
            ],
          },
        ],
      },
      {
        id: 'english-formula-plus',
        title: '잉포 Plus(English Formula Plus)',
        range: 'Book 1 ~ 24',
        variant: 'YELLOW',
        levels: [
          {
            level: 'Level 7',
            books: [
              { book: 'Book 1', title: 'Getting to Know Each Other' },
              { book: 'Book 2', title: 'Inviting a Friend Over' },
              { book: 'Book 3', title: 'Hanging Out in the Park' },
              {
                book: 'Book 4',
                title: 'Telephone Conversation: Making an Appointment',
              },
            ],
          },
          {
            level: 'Level 8',
            books: [
              { book: 'Book 5', title: 'Talking About the Weekend' },
              { book: 'Book 6', title: 'Ordering Fast Food' },
              { book: 'Book 7', title: 'Going Bike Riding' },
              { book: 'Book 8', title: 'Seeing a Doctor' },
            ],
          },
          {
            level: 'Level 9',
            books: [
              { book: 'Book 9', title: 'Shopping for Clothes' },
              { book: 'Book 10', title: 'Appreciating Art Pieces' },
              {
                book: 'Book 11',
                title: 'Going on a Field Trip to the Mud Flats',
              },
              { book: 'Book 12', title: 'Experiencing Real-life Jobs' },
            ],
          },
          {
            level: 'Level 10',
            books: [
              { book: 'Book 13', title: 'Reporting an Emergency' },
              { book: 'Book 14', title: 'Describing a Bad Day' },
              { book: 'Book 15', title: 'Going Grocery Shopping' },
              { book: 'Book 16', title: 'Going to the Library' },
            ],
          },
          {
            level: 'Level 11',
            books: [
              { book: 'Book 17', title: 'Going to the Pharmacy' },
              { book: 'Book 18', title: 'Going Camping' },
              { book: 'Book 19', title: 'Getting a Haircut' },
              {
                book: 'Book 20',
                title: 'Giving Tips for a School Play Tryout',
              },
            ],
          },
          {
            level: 'Level 12',
            books: [
              { book: 'Book 21', title: 'Talking About Winter Break Plans' },
              { book: 'Book 22', title: 'Taking Passport Photos' },
              { book: 'Book 23', title: 'Checking In at the Airport' },
              { book: 'Book 24', title: 'On the Plane' },
            ],
          },
        ],
      },
    ],
  },
];

export const FREE_TRIAL_GUIDE_ITEMS = [
  {
    label: '수업하기',
    description:
      "실제 수업에서 엄마·선생님이 안내하는 부분을 '엄마 가이드' 텍스트로 담았습니다. 수업하는 방법은 등록 후 교사대학 영상으로 교육받으실 수 있으며, 교사 가이드도 함께 제공됩니다.",
  },
  {
    label: 'AI 인증제',
    description:
      '실제 인증제는 암송뿐 아니라 문법·회화 과목까지 교재 한 권 단위로 한 번에 응시하며, 무제한 연습 후 준비가 되었을 때 응시합니다. 무료체험에서는 암송 1레슨만 바로 응시해 볼 수 있습니다.',
  },
  {
    label: '이용 기간',
    description:
      '무료체험은 신청일을 포함해 3일간 이용할 수 있으며, 이후에는 체험 내용을 볼 수 없습니다.',
  },
  {
    label: '신청 횟수',
    description: '무료체험은 계정당 2회까지 신청할 수 있습니다.',
  },
  {
    label: '결과 리포트',
    description:
      '결과 리포트는 확인 후 바로 이미지로 저장해주세요. 따로 서버에 저장되지 않아 이후에는 다시 저장할 수 없습니다.',
  },
  {
    label: '녹음 안내',
    description:
      'AI 인증제는 마이크 권한이 필요합니다. 조용한 곳에서 아이가 직접 말할 수 있도록 도와주세요.',
  },
];

export interface FreeTrialContextItem {
  childName: string;
  status: StatusEnum;
  expireAt: string;
  remainingAttempts: number;
  practiceCount: number;
  stepProgress: FreeTrialStepProgress | null;
  recitation: {
    studentId: number;
    todoId: number;
    accessToken: string;
    paperId: string;
  };
  result: {
    passed: true;
    totalScore: number;
    accuracy: number;
    fluency: number;
    recitationType: RecitationType;
  };
  shareToken: string;
  presetKey: string;
  review: {
    todoId: number;
    title: string;
    link: string;
  };
  childGrade: number;
  childAge: number;
  childGender: string;
}

export interface ReportFreeTrialStepProgressRequest {
  linkToken: string;
  step: 'tutorial' | 'lesson' | 'review' | 'recitation' | 'result';
}

export interface DetourFreeTrialRecitationRequest {
  linkToken: string;
  passed: boolean;
}

export interface CreateShareTokenWithConsentResponse {
  shareToken: 'string';
}

interface FreeTrialResultTypeItem {
  name: string;
  catchphrase: string;
  description: string;
  verse: string;
  tags: string[];
  bestMatchType: string;
  image: string;
  backgroundColor: string;
  accentColor: string;
}

export const FREE_TRIAL_RESULT_TYPE_MAP: Record<
  RecitationType,
  FreeTrialResultTypeItem
> = {
  SAMUEL: {
    name: '사무엘형',
    catchphrase: '한 마디도 땅에 떨어지지 않는',
    description:
      '사무엘이 자라는 동안\n하나님은 그의 말을 하나도 땅에 떨어지지 않게 하셨어요.\n또박또박 정확하게 말했어요!',
    verse: '사무엘상 3:19',
    tags: ['#정확한 발음', '#또박또박', '#말씀 집중'],
    bestMatchType: '다윗형',
    image: '/images/free-trial/SAMUEL.png',
    backgroundColor: '#F1EDFF',
    accentColor: '#6737F4',
  },

  SOLOMON: {
    name: '솔로몬형',
    catchphrase: '지혜롭게 다 기억하는',
    description:
      '지혜를 구한 솔로몬처럼 말씀 내용을 빠짐없이 기억했어요.\n이제 소리와 반복을 조금만 더 다듬으면 최고!',
    verse: '열왕기상 3:5',
    tags: ['#기억력', '#지혜', '#완성형'],
    bestMatchType: '여호수아형',
    image: '/images/free-trial/SOLOMON.png',
    backgroundColor: '#E6F7F2',
    accentColor: '#008B72',
  },

  DAVID: {
    name: '다윗형',
    catchphrase: '말씀이 노래가 되는',
    description:
      '시편을 노래한 다윗처럼\n말씀이 리듬을 타고 자연스럽게 흘러나왔어요.',
    verse: '시편 23편',
    tags: ['#리듬감', '#표현력', '#즐거운 암송'],
    bestMatchType: '사무엘형',
    image: '/images/free-trial/DAVID.png',
    backgroundColor: '#EAF3FF',
    accentColor: '#1674C9',
  },

  JOSHUA: {
    name: '여호수아형',
    catchphrase: '꾸준히 연습해 해내는',
    description:
      '여리고 성을 날마다 돌았던 여호수아처럼\n연습을 거듭해서 멋지게 끝까지 해냈어요!',
    verse: '여호수아 6장',
    tags: ['#꾸준한 연습', '#성실함', '#끝까지 도전'],
    bestMatchType: '솔로몬형',
    image: '/images/free-trial/JOSHUA.png',
    backgroundColor: '#FFF0E8',
    accentColor: '#D84B17',
  },

  PETER: {
    name: '베드로형',
    catchphrase: '망설임 없이 뛰어드는',
    description:
      '물 위로 걸어간 베드로처럼\n망설임 없이 바로 도전해서 끝까지 해냈어요.',
    verse: '마태복음 14:29',
    tags: ['#도전', '#용기', '#첫 도전'],
    bestMatchType: '모세형',
    image: '/images/free-trial/PETER.png',
    backgroundColor: '#FCEAF2',
    accentColor: '#C11B61',
  },

  MOSES: {
    name: '모세형',
    catchphrase: '끝까지 해내는',
    description:
      '말을 잘 못한다고 했던 모세도 하나님과 함께 해냈어요.\n다시 도전해서 멋지게 끝까지 완주했어요!',
    verse: '출애굽기 4:12',
    tags: ['#끈기', '#다시 도전', '#성장'],
    bestMatchType: '베드로형',
    image: '/images/free-trial/MOSES.png',
    backgroundColor: '#FFF5D9',
    accentColor: '#A76A00',
  },
};

export enum RecitationResultStatusEnum {
  /** 응시 전/판정 가능 회차 없음 */
  NOT_ATTEMPTED = 'NOT_ATTEMPTED',
  /** 응시 진행 중 */
  IN_PROGRESS = 'IN_PROGRESS',
  /** 제출됨 - 분석 대기, 잠시 후 재호출 */
  ANALYZING = 'ANALYZING',
  /** 판정 완료 - 리포트 점수 유효 */
  PASSED = 'PASSED',
  /** 판정 완료 - 리포트 점수 유효 */
  FAILED = 'FAILED',
}

export interface FreeTrialRecitationResult {
  status: RecitationResultStatusEnum;
  childName: string;
  totalScore: number | null;
  accuracy: number | null;
  fluency: number | null;
  pronunciation: number | null;
  completeness: number | null;
  recitationType: RecitationType;
  judgedAt: string | null;
  chunks: [
    {
      text: string;
      recognizedText: string | null;
      correct: boolean;
      score: number | null;
    },
  ];
  remainingAttempts: number;
}

export interface FreeTrialSharedInfo {
  childName: string;
  recitationType: RecitationType;
}
